import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Info } from "lucide-react";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm({ sourcePage = "contact", onSuccess, compact = false, defaultService = "" }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", country: "",
    service_interest: defaultService, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source_page: sourcePage });
      toast.success("Your enquiry has been received.");
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", phone: "", country: "", service_interest: "", message: "" });
      onSuccess?.();
    } catch {
      toast.error("Submission failed. Please try again, or email contact@datameridianconsulting.com.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-white border border-[var(--dm-border)] px-4 py-3 text-sm text-[var(--dm-text)] focus:outline-none focus:border-[var(--dm-emerald)] transition-colors";
  const labelCls = "block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--dm-muted)] mb-2";

  if (submitted) {
    return (
      <div data-testid="contact-form-success" className="border border-[var(--dm-border)] bg-white p-8">
        <div className="w-10 h-10 grid place-items-center bg-[var(--dm-emerald)] text-white">
          <Check className="w-5 h-5" />
        </div>
        <div className="font-serif text-2xl text-[var(--dm-navy)] mt-6">Thank you for contacting DataMeridian Consulting.</div>
        <p className="text-[var(--dm-muted)] mt-4 leading-relaxed">
          We have received your enquiry successfully. One of our consultants will contact you within one business day.
        </p>
        <div className="mt-6 flex items-start gap-3 border-t border-[var(--dm-border)] pt-6 text-xs text-[var(--dm-muted)] leading-relaxed">
          <Info className="w-4 h-4 mt-0.5 text-[var(--dm-emerald)] shrink-0" />
          <div>
            A confirmation has been forwarded to our team at <a className="text-[var(--dm-emerald)] font-semibold" href="mailto:contact@datameridianconsulting.com">contact@datameridianconsulting.com</a>. If your enquiry is urgent, feel free to call us at <a className="text-[var(--dm-emerald)] font-semibold" href="tel:+919833624208">+91 98336 24208</a>.
          </div>
        </div>
        <button data-testid="contact-form-reset" onClick={() => setSubmitted(false)} className="dm-btn-outline mt-6">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className={`grid ${compact ? 'gap-4' : 'gap-5'}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Full name</label>
          <input required data-testid="cf-name" className={inputCls} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
        </div>
        <div>
          <label className={labelCls}>Work email</label>
          <input required type="email" data-testid="cf-email" className={inputCls} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Company</label>
          <input data-testid="cf-company" className={inputCls} value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} />
        </div>
        <div>
          <label className={labelCls}>Phone (optional)</label>
          <input data-testid="cf-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Country</label>
          <input data-testid="cf-country" className={inputCls} value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} />
        </div>
        <div>
          <label className={labelCls}>Service of interest</label>
          <select data-testid="cf-service" className={inputCls} value={form.service_interest} onChange={(e) => setForm({...form, service_interest: e.target.value})}>
            <option value="">Select an option</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>How can we help?</label>
        <textarea required rows={compact ? 3 : 5} data-testid="cf-message" className={inputCls} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} />
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={loading} data-testid="cf-submit" className="dm-btn-primary">
          {loading ? "Sending…" : "Talk to a Specialist"}
        </button>
        <p className="text-xs text-[var(--dm-muted)]">A consultant will respond within one business day.</p>
      </div>
    </form>
  );
}
